import axios from "axios"
import { PayloadAction, createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { getBaseEndpoint } from "../../features/endpoints/endpointManager"
import { DataStatus } from "../enums"

export type QuicksearchOptionType = {
  value: string
  label: string
  [key: string]: string | undefined
}

export const searchEndpoint = getBaseEndpoint() + "/search"

interface SearchServiceState {
  practiceSearchResults: QuicksearchOptionType[]
  providerSearchResults: QuicksearchOptionType[]
  referralSearchResults: QuicksearchOptionType[]
  practiceSearchResultsStatus: DataStatus
  referralSearchResultsStatus: DataStatus
  providerSearchResultsStatus: DataStatus
}

const initialSearchServiceState: SearchServiceState = {
  practiceSearchResults: [],
  providerSearchResults: [],
  referralSearchResults: [],
  practiceSearchResultsStatus: DataStatus.LOADING,
  providerSearchResultsStatus: DataStatus.LOADED,
  referralSearchResultsStatus: DataStatus.LOADING,
}

const SEARCH_SERVICE_SLICE_NAME = "searchService"

export const searchServiceSlice = createSlice({
  name: SEARCH_SERVICE_SLICE_NAME,
  initialState: initialSearchServiceState,
  reducers: {
    practiceSearchResultsUpdated: (
      state: { practiceSearchResults: QuicksearchOptionType[] },
      { payload }: PayloadAction<QuicksearchOptionType[]>,
    ) => {
      state.practiceSearchResults = payload
    },
    providerSearchResultsUpdated: (
      state: SearchServiceState,
      { payload }: PayloadAction<QuicksearchOptionType[]>,
    ) => {
      state.providerSearchResults = payload
    },
    referralSearchResultsUpdated: (
      state: SearchServiceState,
      { payload }: PayloadAction<QuicksearchOptionType[]>,
    ) => {
      state.referralSearchResults = payload
    },
    practiceSearchResultsStatusUpdated: (
      state: SearchServiceState,
      { payload }: PayloadAction<DataStatus>,
    ) => {
      state.practiceSearchResultsStatus = payload
    },
    providerSearchResultsStatusUpdated: (
      state: SearchServiceState,
      { payload }: PayloadAction<DataStatus>,
    ) => {
      state.providerSearchResultsStatus = payload
    },
    referralSearchResultsStatusUpdated: (
      state: SearchServiceState,
      { payload }: PayloadAction<DataStatus>,
    ) => {
      state.referralSearchResultsStatus = payload
    },
    resetSearchState: (state: SearchServiceState) => {
      Object.assign(state, initialSearchServiceState)
    },
  },
})

export default searchServiceSlice.reducer

export const {
  practiceSearchResultsUpdated,
  providerSearchResultsUpdated,
  referralSearchResultsUpdated,
  practiceSearchResultsStatusUpdated,
  providerSearchResultsStatusUpdated,
  referralSearchResultsStatusUpdated,
  resetSearchState,
} = searchServiceSlice.actions

export const selectPracticeSearchResultsStatus = (state: {
  searchService: SearchServiceState
}): DataStatus => {
  return state[SEARCH_SERVICE_SLICE_NAME].practiceSearchResultsStatus
}

export const selectPracticeSearchResults = (state: {
  searchService: SearchServiceState
}): QuicksearchOptionType[] => {
  return state[SEARCH_SERVICE_SLICE_NAME].practiceSearchResults
}

export const selectProviderSearchResultsStatus = (state: {
  searchService: SearchServiceState
}): DataStatus => {
  return state[SEARCH_SERVICE_SLICE_NAME].providerSearchResultsStatus
}

export const selectProviderSearchResults = (state: {
  searchService: SearchServiceState
}): QuicksearchOptionType[] => {
  return state[SEARCH_SERVICE_SLICE_NAME].providerSearchResults
}

export const selectReferralSearchResults = (state: {
  searchService: SearchServiceState
}): QuicksearchOptionType[] => {
  return state[SEARCH_SERVICE_SLICE_NAME].referralSearchResults
}

export const selectReferralSearchResultsStatus = (state: {
  searchService: SearchServiceState
}): DataStatus => {
  return state[SEARCH_SERVICE_SLICE_NAME].referralSearchResultsStatus
}

const body = (searchTerm: string, searchFilter?: string) => {
  return {
    query: searchTerm,
    filter: searchFilter,
  }
}

export interface SearchThunkArgs {
  searchTerm: string
  searchFilter?: string
  licenseStatuses?: string[] | null
  count?: number // pageSize
  offset?: number
  searchIndex?: string
  activeFeatureFlags?: string[]
  scoringProfile?: string
}

interface SearchResponse {
  id: string
  name: string
  secondLine: string
}

interface ReferralSearchResponse {
  id: string
  providerId: string
  caseNumber: string
  memberFirstName: string
  memberLastName: string
  providerFirstName: string
  providerLastName: string
  status: string
  dateCreated: string
}

export const practiceSearch = createAsyncThunk<void, SearchThunkArgs>(
  `${SEARCH_SERVICE_SLICE_NAME}/practiceSearch`,
  async ({ searchTerm, searchFilter }, { dispatch }) => {
    try {
      dispatch(practiceSearchResultsStatusUpdated(DataStatus.SENDING))
      const response = await axios.post<SearchResponse[]>(
        `${searchEndpoint}/practiceSearch`,
        body(searchTerm, searchFilter),
      )
      if (response.status === 200) {
        dispatch(
          practiceSearchResultsUpdated(
            response.data.map((item) => ({
              value: item.id,
              label: item.name,
              secondLine: item.secondLine,
              key: "practice",
            })),
          ),
        )
        dispatch(practiceSearchResultsStatusUpdated(DataStatus.LOADED))
      } else {
        throw new Error(`${response.status}: ${response.statusText}`)
      }
    } catch (error) {
      dispatch(practiceSearchResultsStatusUpdated(DataStatus.ERROR))
      throw error
    }
  },
)

export const providerSearch = createAsyncThunk<void, SearchThunkArgs>(
  `${SEARCH_SERVICE_SLICE_NAME}/providerSearch`,
  async ({ searchTerm, searchFilter }, { dispatch }) => {
    try {
      dispatch(providerSearchResultsStatusUpdated(DataStatus.SENDING))
      const response = await axios.post<SearchResponse[]>(
        `${searchEndpoint}/providerSearch`,
        body(searchTerm, searchFilter),
      )
      if (response.status === 200) {
        dispatch(
          providerSearchResultsUpdated(
            response.data.map((item) => ({
              value: item.id,
              label: item.name,
              secondLine: item.secondLine,
              key: "provider",
            })),
          ),
        )
        dispatch(providerSearchResultsStatusUpdated(DataStatus.LOADED))
      } else {
        throw new Error(`${response.status}: ${response.statusText}`)
      }
    } catch (error) {
      dispatch(providerSearchResultsStatusUpdated(DataStatus.ERROR))
      throw error
    }
  },
)

export const referralSearch = createAsyncThunk<void, SearchThunkArgs>(
  `${SEARCH_SERVICE_SLICE_NAME}/referralSearch`,
  async ({ searchTerm, searchFilter }, { dispatch }) => {
    try {
      dispatch(referralSearchResultsStatusUpdated(DataStatus.SENDING))
      const response = await axios.post<ReferralSearchResponse[]>(
        `${searchEndpoint}/referralSearch`,
        body(searchTerm, searchFilter),
      )
      if (response.status === 200) {
        dispatch(
          referralSearchResultsUpdated(
            response.data.map((item) => ({
              value: item.id,
              label: `${item.memberFirstName} ${item.memberLastName} / ${item.providerFirstName} ${item.providerLastName}`,
              secondLine: `Case #${item.caseNumber}, ${item.dateCreated.split(" ")[0]}, ${item.status}`,
              caseNumber: item.caseNumber,
              key: "referral",
            })),
          ),
        )
        dispatch(referralSearchResultsStatusUpdated(DataStatus.LOADED))
      } else {
        throw new Error(`${response.status}: ${response.statusText}`)
      }
    } catch (error) {
      dispatch(referralSearchResultsStatusUpdated(DataStatus.ERROR))
      throw error
    }
  },
)

export const parseAdvancedSearchResponse = (
  result: ProviderSearchResult | PracticeSearchResult,
) => {
  Object.keys(result).map((key) => {
    const obj = result[key as keyof typeof result] as string | number
    if (typeof obj == "string") {
      if ((obj as string).startsWith("[")) {
        const str = obj
          .replace(/\ '/g, ' "')
          .replace(/'\ /g, '" ')
          .replace(/',/g, '",')
          .replace(/,'/g, ',"')
          .replace(/':/g, '":')
          .replace(/\['/g, '["')
          .replace(/'\]/g, '"]')
        try {
          ;(result[key as keyof typeof result] as unknown) = JSON.parse(str)
        } catch (error) {
          console.error(error, str)
        }
      }
    }
  })
  return result
}
